import React, { useEffect, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { getVizTokens } from '../analytics/vizTokens';
import {
    STATE, groupDigits, isImpossible, looksWrong, read, unitChoices,
} from './expression';
import CountKeypad from './CountKeypad';

/**
 * One item, filling the screen.
 *
 * Every band has a settled height so nothing shifts as you move between items -
 * the name box is two lines tall whether the item is "Curd" or "Everest Garam
 * Masala Powder", and the line that carries warnings is always there whether it
 * has anything to say or not. A layout that jumps under a thumb already on its
 * way down is a layout that mistypes.
 */
function CountStage(props) {
    const {
        item, index, total, raw, unit, previous, next,
        onChange, onUnit, onMove, onDone,
    } = props;
    const theme = useTheme();
    const tokens = useMemo(() => getVizTokens(theme.palette.mode === 'dark'),
        [theme.palette.mode]);
    const units = useMemo(() => unitChoices(item.measurement_unit), [item]);
    const active = units.find((u) => u.label === unit) || units[0];

    const reading = read(raw);
    // What this actually commits, in the unit the item is stored in.
    const stored = reading.state === STATE.OK
        ? Math.round(reading.value * active.factor) : null;
    const stop = isImpossible(stored, item.measurement_unit);
    const odd = stop ? null : looksWrong(stored, item.current_inventory);
    const isEmpty = !raw;

    const stageRef = useRef(null);
    useEffect(() => {
        // A hardware keyboard should work without hunting for a focus target.
        if (stageRef.current) stageRef.current.focus({ preventScroll: true });
    }, [index]);

    // Each press is expressed as a change to whatever the entry currently is,
    // not to whatever it was when this render happened. Reading the prop would
    // drop a digit whenever two taps land inside one frame - which a fast thumb
    // on a keypad does regularly, and which would be invisible until the wrong
    // number reached the books.
    const press = (key) => {
        onChange((current) => {
            const text = current || '';
            if (key === 'del') return text.slice(0, -1);
            if (key === '+' || key === '*') {
                if (!text) return text;
                return /[+*]\s*$/.test(text)
                    ? text.replace(/[+*]\s*$/, key) : text + key;
            }
            const base = text === '0' ? '' : text;
            if ((base + key).replace(/\D/g, '').length > 9) return text;
            return base + key;
        });
    };

    const onKeyDown = (event) => {
        if (/^[0-9]$/.test(event.key)) { press(event.key); event.preventDefault(); }
        else if (event.key === '+' || event.key === '*') { press(event.key); event.preventDefault(); }
        else if (event.key === 'Backspace') {
            if (raw) press('del'); else onMove(-1);
            event.preventDefault();
        } else if (event.key === 'Enter') { advance(); event.preventDefault(); }
        else if (event.key === 'ArrowLeft') { onMove(-1); event.preventDefault(); }
        else if (event.key === 'ArrowRight') { advance(); event.preventDefault(); }
    };

    const advance = () => {
        if (stop) return;
        if (index + 1 >= total) onDone();
        else onMove(1);
    };

    return (
        <Box
            ref={stageRef}
            tabIndex={-1}
            onKeyDown={onKeyDown}
            sx={{ outline: 'none', display: 'flex', flexDirection: 'column',
                minHeight: 0, flexGrow: 1 }}
        >
            {/* The entry just committed - a receipt, so you never look back. */}
            <Box onClick={() => onMove(-1)} sx={{
                px: 2, py: 0.5, minHeight: 28, cursor: previous ? 'pointer' : 'default',
                color: tokens.textMuted, fontSize: '0.75rem',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                borderBottom: 1, borderColor: 'divider',
            }}>
                {previous ? (
                    <>‹ {previous.item.item_name} &nbsp;
                        <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                            {previous.stored === null ? '—'
                                : `${groupDigits(previous.stored)} ${previous.item.measurement_unit}`}
                        </Box>
                    </>
                ) : ' '}
            </Box>

            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column',
                justifyContent: 'center', px: 2, minHeight: 0 }}>
                <Typography variant="overline" sx={{ color: tokens.textMuted, lineHeight: 1.4 }}>
                    Item {index + 1} of {total}
                </Typography>
                <Typography sx={{
                    fontSize: '1.5rem', fontWeight: 650, lineHeight: 1.15,
                    minHeight: '2.3em', display: 'flex', alignItems: 'center',
                    textWrap: 'balance',
                }}>
                    {item.item_name}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'baseline',
                    justifyContent: 'flex-end', gap: 0.75, minHeight: 56 }}>
                    <Typography sx={{
                        fontSize: '2.4rem', fontWeight: 600, lineHeight: 1,
                        fontVariantNumeric: 'tabular-nums',
                        color: raw ? 'text.primary' : 'divider',
                    }}>
                        {raw || '0'}
                    </Typography>
                    <Typography onClick={() => {
                        const other = units.find((u) => u.label !== active.label);
                        if (other) onUnit(other.label);
                    }} sx={{
                        fontSize: '1.05rem', fontWeight: units.length > 1 ? 700 : 500,
                        color: units.length > 1 ? 'primary.main' : tokens.textMuted,
                        cursor: units.length > 1 ? 'pointer' : 'default',
                    }}>
                        {active.label}
                    </Typography>
                </Box>

                {/* One line, always present, so nothing below it ever moves. */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between',
                    gap: 1, minHeight: 34, fontSize: '0.75rem' }}>
                    <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                        {Number(item.current_inventory) > 0
                            ? `last ${groupDigits(item.current_inventory)} ${item.measurement_unit}`
                            : 'never counted'}
                    </Typography>
                    <Typography variant="caption" sx={{ textAlign: 'right' }}>
                        {stop ? (
                            <Box component="span" sx={{ color: tokens.critical, fontWeight: 600 }}>
                                {stop}
                            </Box>
                        ) : reading.state === STATE.INVALID ? (
                            <Box component="span" sx={{ color: tokens.critical }}>
                                Numbers, + and × only
                            </Box>
                        ) : odd ? (
                            <Box component="span" sx={{ color: 'warning.main' }}>
                                {odd.kind === 'high' ? 'Much more' : 'Much less'} than
                                last {groupDigits(odd.previous)}
                            </Box>
                        ) : (stored !== null && active.factor !== 1) ? (
                            // The committed figure, in the unit it is stored in.
                            // Tapping the wrong unit key is a thousand-fold
                            // error, so it is spelled out before you advance.
                            <Box component="span" sx={{ color: tokens.good, fontWeight: 600 }}>
                                = {groupDigits(stored)} {item.measurement_unit}
                            </Box>
                        ) : (stored !== null && /[+*]/.test(raw)) ? (
                            <Box component="span" sx={{ color: tokens.good }}>
                                = {groupDigits(stored)} {item.measurement_unit}
                            </Box>
                        ) : reading.state === STATE.INCOMPLETE && reading.value !== null ? (
                            <Box component="span" sx={{ color: tokens.textSecondary }}>
                                = {groupDigits(Math.round(reading.value * active.factor))} so far
                            </Box>
                        ) : ' '}
                    </Typography>
                </Box>

                <Box onClick={() => onMove(1)} sx={{
                    minHeight: 24, color: tokens.textMuted, fontSize: '0.75rem',
                    cursor: next ? 'pointer' : 'default', pb: 0.5,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                    {next ? `next › ${next.item_name}` : 'last item'}
                </Box>
            </Box>

            <CountKeypad
                units={units}
                unit={active}
                isEmpty={isEmpty}
                canDelete={Boolean(raw)}
                onDigit={press}
                onOperator={press}
                onDelete={() => press('del')}
                onBack={() => onMove(-1)}
                onNext={advance}
                onUnit={(choice) => onUnit(choice.label)}
            />
        </Box>
    );
}

export default CountStage;
